/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import React, { ReactNode, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import { getLuxeFlags } from '../../selectors/featureFlagsSelector';
import { getCurrentStore } from '../../selectors/metaData';
import Icon from '../../atoms/Icon';
import styles from './dialog.pcss';
import noop from '../../utils/noop';
import { FLEEK_STORE } from '../../../constants/constant';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';

const mapStateToProps = state => ({
  luxeFlags: getLuxeFlags(state),
  storeName: getCurrentStore(state),
});

const connector = connect(mapStateToProps);

type Fn = (maskClick?: boolean) => void;

interface DialogProps {
  onCancel?: Fn;
  showDialog?: boolean;
  children?: ReactNode | string;
  type?: 'drawer' | 'modal';
  fullWidth?: boolean;
  maskClosable?: boolean;
  closeIcon?: boolean;
  drawerBottomCloseIcon?: boolean;
  customClass?: string;
  customContentClass?: string;
  isMyJioRedirection?: boolean;
  customContentWrapperClass?: string;
  videoCloseIcon?: boolean;
  customContentClassAddOns?: string;
  displayContent?: string;
  isFromPDP?: boolean;
  isFilter?: boolean;
  modalMaxHeight?: string;
  isSubscriptionModal?: boolean;
  maskStyles?: object;
  id?: string;
  enableRushDelivery?: boolean;
  isShowAddAddress?: boolean;
  opacityChange?: boolean;
  role?: string;
  ariaLevel?: number;
  isCloseIconHidden?: string;
}

const cx = classNames.bind(styles);

const Dialog = (props: DialogProps): JSX.Element => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLDivElement | null>(null);
  const openButtonRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const {
    children,
    showDialog,
    onCancel,
    type = 'drawer',
    fullWidth,
    maskClosable = true,
    closeIcon = true,
    drawerBottomCloseIcon = false,
    modalStyles = {},
    customClass = '',
    luxeFlags,
    storeName,
    customContentClass,
    callbackOnVisible = noop,
    isMyJioRedirection = false,
    customContentWrapperClass = '',
    videoCloseIcon = false,
    customContentClassAddOns = '',
    displayContent = '',
    isFromPDP = false,
    isFilter = false, 
    modalMaxHeight,
    isSubscriptionModal = false,
    maskStyles = {},
    id = '',
    enableRushDelivery = false,
    isShowAddAddress = false,
    role,
    ariaLevel,
    opacityChange,
    ariaLabel="DialogBox",
    isCloseIconHidden = false,
    isMembershipArtical = false
  } = props;
  // console.log('Modal Props -> ', props)
  const { isThemeEnable } = luxeFlags;
  const luxe = isThemeEnable === 'ON' && storeName === 'luxe';
  const isFleek = storeName === FLEEK_STORE;
  const dialogClasses: string = cx(
    'dialog',
    type,
    customClass,
    { open: showDialog },
    { 'my-jio-content-wrapper': isMyJioRedirection }
  );
  const maskClasses: string = cx({ [`dialog-mask`]: true });
  const maskRushClasses: string = cx({ [`dialog-mask-rush`]: true });
  const maskMembershipClasses: string = cx({ [`dialog-mask-membership`]: true });
  const maskClassesChangedOpacity: string = cx({ [`cancel-return-container-dialog-mask`]: true });
  const contentWrapperClass: string = cx(
    {
      [customContentWrapperClass
        ? `${customContentWrapperClass}-content-wrapper`
        : `${type}-content-wrapper`]: true
    },
    { customModalWidth: fullWidth && type === 'modal' },
    { 'drawer-bottomClose-wrapper-class': drawerBottomCloseIcon }
  );
  const contentWrapperClassRushClasses: string = cx({ [`cart-address-list-content-wrapper-rush`]: true });
  const contentClass: string = cx(
    { [customContentClass ? `${customContentClass}-content` : `${type}-content`]: true },
    { [`modal-radius`]: !fullWidth && type === 'modal' },
    { 'drawer-bottomClose-class': drawerBottomCloseIcon },
    customContentClassAddOns
  );

  const isIconReq: boolean = type === 'drawer';
  const iconStyle = {
    fontSize: luxe ? '24px' : '22px',
    color: 'white',
    width: '22px'
  };

  const subscriptionIconStyle = {
    fontSize: '14px',
    color: 'white',
    width: '40px',
    height: '40px',
    borderRadius: '40px',
    background: '#000',
    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
    textAlign: 'center',
    alignContent: 'center'
  };

  const handleGAOnCancel = () => {
    if (id.includes('return_fee_post-order')) {
      gtmEventPush({
        event: 'return_fee_halfcard_interaction',
        event_category: 'return fee halfcard interaction',
        event_action: 'cross',
        page_name: 'return fee halfcard screen'
      });
    }
  };

  const clickHandler = (): void => {
    document.body.style = null;
    handleGAOnCancel();
    onCancel();
  };

  const maskCloseHandler = (): void => {
    if (maskClosable) {
      document.body.style = null;
      handleGAOnCancel();
      onCancel(true);
    }
  };

  useEffect(() => {
    if (showDialog) {
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      callbackOnVisible ? callbackOnVisible() : noop;
      openButtonRef.current = document.activeElement as HTMLElement;
      modalRef.current?.focus();
      contentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.touchAction = originalTouchAction;
      };
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
      openButtonRef.current?.focus();
    }
  }, [showDialog]);

  useEffect(() => {
    if (!showDialog || !modalRef.current) return;
    const modalElement = modalRef.current;
    const focusableElements = modalElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as Element);
      if (event.key === "Tab") {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        let nextIndex = currentIndex + 1;
        if (nextIndex >= focusableElements.length) nextIndex = 0;
        focusableElements[nextIndex].focus();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        let prevIndex = currentIndex - 1;
        if (prevIndex < 0) prevIndex = focusableElements.length - 1;
        focusableElements[prevIndex].focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDialog]);
  // useEffect(() => {
  //   const app = document.getElementById('app');
  //   var appDesc = app?.getElementsByTagName('*') ?? [];
  //   for (let el of appDesc) {
  //     if (showDialog) {
  //       el.setAttribute('aria-hidden', 'true');
  //     } else {
  //       el.setAttribute('aria-hidden', 'false');
  //     }
  //   }

  //   var ancestors = document.querySelectorAll('#popup');
  //   for (let ancestor of ancestors) {
  //     let targetDisplay = 'block';

  //     let computedStyle = window.getComputedStyle(ancestor);
  //     if (computedStyle.display === targetDisplay) {
  //       ancestor?.setAttribute('aria-hidden', 'false');
  //       var descendents = ancestor?.getElementsByTagName('*') ?? [];
  //       for (let el of descendents) {
  //         el.setAttribute('aria-hidden', 'false');
  //       }
  //     }
  //   }
  // }, [showDialog]);

  if (!showDialog && type === 'modal') {
    return <></>;
  }

  const getStyles = () => {
    // isFleek ? (isFilter ? { background: '#2E343A' } : {}) : {}
    let styleObj = {};
    if(modalMaxHeight){
      styleObj = {...styleObj, 'max-height':modalMaxHeight};
    }
    if(isFleek && (isFilter || isSubscriptionModal)){
      styleObj = {...styleObj,  background: '#2E343A'}
    }
    return styleObj;
  }

  return (
    <div
      id="popup"
      className={cx(
        dialogClasses,
        { ['topStyle-modal-rush']: enableRushDelivery },
        { ['modal-rush']: enableRushDelivery && isShowAddAddress }
      )}
      style={modalStyles}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      ref={modalRef}
      {...(props.ariaLabel !== undefined && props.ariaLabel !== ''
        ? { 'aria-label': `${props.ariaLabel}` }
        : {})}
    >
      <div
        className={cx(
          opacityChange ? maskClassesChangedOpacity : maskClasses,
          { [maskRushClasses]: enableRushDelivery && !isShowAddAddress },
          { [maskMembershipClasses]: isMembershipArtical }
        )}
        role="presentation"
        onClick={maskCloseHandler}
      />
      <div
        className={cx(
          contentWrapperClass,
          { [contentWrapperClassRushClasses]: enableRushDelivery && !isShowAddAddress },
          {
            ['cart-address-list-content-wrapper-add-address-rush']:
              enableRushDelivery && isShowAddAddress
          }
        )}
      >
        {videoCloseIcon && !drawerBottomCloseIcon && (
          <div
            id="popup_close"
            className={cx('video-close')}
            role="button"
            onClick={clickHandler}
            // tabIndex={0}
            {...(isCloseIconHidden ? { 'aria-hidden': 'true' } : { 'aria-label': 'close' })}
          >
            <Icon icon="icon-Close" styles={iconStyle} ariaHidden />
          </div>
        )}
        {isIconReq && closeIcon && !drawerBottomCloseIcon && (
          <div
            id="popup_close"
            className={cx('close')}
            role="button"
            onClick={clickHandler}
            ref={closeButtonRef}
            tabIndex={isCloseIconHidden ? -1 : 0}
            {...(isCloseIconHidden ? { 'aria-hidden': 'true' } : { 'aria-label': 'close' })}
          >
            <Icon icon="icon-Close-2" styles={iconStyle} ariaHidden />
          </div>
        )}
        <div
          className={cx(
            contentClass,
            { ['cart-address-list-dialog-content-rush']: enableRushDelivery },
            { ['cart-address-list-dialog-content-fleek-rush']: enableRushDelivery && isFleek },
            {
              ['cart-address-list-dialog-content-add-address-rush']:
                enableRushDelivery && isShowAddAddress
            }
          )}
          style={getStyles()}
          ref={contentRef}
          role={role}
          aria-level={ariaLevel}
          // tabIndex={0}
        >
          {children}
        </div>
        {isIconReq && closeIcon && drawerBottomCloseIcon && (
          <div
            id="popup_close"
            className={cx('bottom-close')}
            role="presentation"
            onClick={clickHandler}
            // tabIndex={0}
          >
            <Icon icon="icon-Close-2" styles={iconStyle} ariaLabel="close" />
          </div>
        )}

        {isMembershipArtical && (
          <div
            id="popup_close"
            className={cx('bottom-close')}
            role="button"
            onClick={clickHandler}
            // tabIndex={0}
            {...(isCloseIconHidden ? { 'aria-hidden': 'true' } : { 'aria-label': 'close' })}
          >
            <Icon icon="icon-Close" styles={subscriptionIconStyle} ariaHidden />
          </div>
        )}
      </div>
    </div>
  );
};

export default connector(Dialog);
